import { Activity, Collection, Copier, IAttribute, IRefreshTable, ISelectOption, LogicAppsProperty, Metamodel, Owned, ParameterConfig, ParameterValue } from 'shell-models-lib';
import { PowerBILinkedService } from '../linkedservice/powerbi.linkedservice';
import { HTTP_VERBS, WebExtensibleUtil } from 'app/shared/utils/web-extensible.util';

export const enum CommitMode {
    PARTIAL_BATCH = 'PartialBatch',
    TRANSACTIONAL = 'Transactional'
}

export const enum RefreshType {
    FULL = 'full',
    CLEAR_VALUES = 'clearValues',
    CALCULATE = 'calculate',
    DATA_ONLY = 'dataOnly',
    AUTOMATIC = 'automatic',
    DEFRAGMENT = 'defragment'
}

@Metamodel.model({
    name: 'PBISemanticModelRefresh',
    referable: true,
})
export class SemanticModelRefreshActivity extends Activity {
    public static readonly WORKSPACEID: IAttribute;
    public static readonly DATASETID: IAttribute;
    public static readonly TABLEIDS: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly WAIT_ON_COMPLETION: IAttribute;
    public static readonly MAX_PARALLELISM: IAttribute;
    public static readonly RETRY_COUNT: IAttribute;
    public static readonly SEMANTIC_MODEL_REFRESH_TIMEOUT: IAttribute;
    public static readonly COMMIT_MODE: IAttribute;
    public static readonly REFRESH_TYPE: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ConnectionLabel,
        constraint: {
            required: true,
            validator: WebExtensibleUtil.validateConnection
        },
        reference: true,
        type: PowerBILinkedService.MODEL
    })
    public powerBILinkedService?: PowerBILinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    public path?: string = '';
    public connector: string = 'powerbi';
    public connectorOptions: Array<ISelectOption<string>> = [];

    /**
     * @deprecated NOTE anredd - Kept only for documentation purposes
     */
    @Metamodel.attribute({
        type: LogicAppsProperty.MODEL,
        collection: Collection.Array,
    })
    public properties?: LogicAppsProperty[] = [];

    @Metamodel.attribute({
        display: stringResources.Workspace,
        association: 'WORKSPACEID',
        templatable: true,
        accepts: [ 'String' ]
    })
    public groupId?: string;

    @Metamodel.attribute({
        display: stringResources.SemanticModel,
        association: 'DATASETID',
        templatable: true,
        accepts: [ 'String' ],
        constraint: {
            required: true
        }
    })
    public datasetId?: string;
    
    @Metamodel.attribute({
        association: 'TABLEIDS',
        templatable: true,
        accepts: [ 'String', 'Array' ]
    })
    public tableIds?: IRefreshTable[];

    @Metamodel.attribute({
        display: stringResources.WaitOnCompletion,
        association: 'WAIT_ON_COMPLETION',
        templatable: true,
        accepts: [ 'String', 'Bool' ]
    })
    public waitOnCompletion?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.MaxParallelism,
        association: 'MAX_PARALLELISM',
        templatable: true,
        accepts: [ 'String', 'Int' ]
    })
    public maxParallelism?: number;

    @Metamodel.attribute({
        display: stringResources.RetryCount,
        association: 'RETRY_COUNT',
        templatable: true,
        accepts: [ 'String', 'Int' ]
    })
    public retryCount?: number;

    @Metamodel.attribute({
        display: stringResources.SemanticModelRefreshTimeout,
        association: 'SEMANTIC_MODEL_REFRESH_TIMEOUT',
        templatable: true,
        accepts: [ 'String' ]
    })
    public semanticModelRefreshTimeout?: string;

    @Metamodel.attribute({
        display: stringResources.CommitMode,
        association: 'COMMIT_MODE',
        templatable: true,
        accepts: [ 'String' ]
    })
    public commitMode?: string = CommitMode.TRANSACTIONAL;

    @Metamodel.attribute({
        display: stringResources.RefreshType,
        association: 'REFRESH_TYPE',
        templatable: true,
        accepts: [ 'String' ]
    })
    public type?: string;

    public headers = [ Owned.create<ParameterConfig>(ParameterConfig.MODEL, this), Owned.create<ParameterConfig>(ParameterConfig.MODEL, this)]
        .map((pc, index) => {
            if (index === 0) {
                pc.name = 'content-type';
                pc.value = 'application/json';
            }

            if (index === 1) {
                pc.name = 'Parition';
                pc.value = 'DimCustomer';
            }

            return pc;
        }
    );

    public body = {
        type: 'Full'
    };

    public relativeUrl = '/refreshes';
    public method: HTTP_VERBS = 'POST';

    public onActivityCloned(clonedActivity: SemanticModelRefreshActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.powerBILinkedService?.parameterized) {
                clonedActivity.powerBILinkedService = Copier.clone(
                    context,
                    [this.powerBILinkedService],
                    undefined,
                    true
                )[0] as PowerBILinkedService;
            }
        }
    }

    public addProperty(parent?: LogicAppsProperty): LogicAppsProperty {
        const property = Owned.create<LogicAppsProperty>(LogicAppsProperty.MODEL, this);
        if (parent) {
            parent.children.push(property);
        } else {
            this.properties.push(property);
        }
        return property;
    }

    public filledPath(): string {
        let filledPath = this.path;
        this.properties.forEach((property) => {
            filledPath = filledPath.replace('{' + property.name + '}', property.value);
        });
        return filledPath;
    }

    public propertiesIncludingChildren(array: LogicAppsProperty[] = this.properties): LogicAppsProperty[] {
        let result: LogicAppsProperty[] = [];
        array.forEach((item) => {
            result.push(item);
            if (item.children) {
                result = result.concat(this.propertiesIncludingChildren(item.children));
            }
        });
        return result;
    }
}